import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { MapPin, Phone, ExternalLink, Plus, Loader2, Lock, Edit2, Trash2, X, Check, Search, RefreshCw } from 'lucide-react';
import { db } from '../firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, where, writeBatch, getDoc, setDoc } from 'firebase/firestore';

const AdminOutletDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [outlets, setOutlets] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState(null);
  
  const [vercelHook, setVercelHook] = useState('');
  const [isSavingHook, setIsSavingHook] = useState(false);
  const [isTriggeringRebuild, setIsTriggeringRebuild] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    link: '',
    phone: '',
    zone: '',
    isHeadOffice: false
  });

  useEffect(() => {
    if (isAuthenticated) {
      fetchOutlets();
      fetchVercelHook();
    }
  }, [isAuthenticated]);

  const fetchVercelHook = async () => {
    try {
      const docRef = doc(db, "settings", "vercel");
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setVercelHook(docSnap.data().hookUrl || '');
      }
    } catch (error) {
      console.error("Error fetching Vercel hook:", error);
    }
  };

  const handleSaveHook = async (e) => {
    e.preventDefault();
    setIsSavingHook(true);
    try {
      const docRef = doc(db, "settings", "vercel");
      await setDoc(docRef, { hookUrl: vercelHook }, { merge: true });
      toast.success("Vercel Deploy Hook saved successfully!");
    } catch (error) {
      console.error("Error saving Vercel hook:", error);
      toast.error("Failed to save Vercel Deploy Hook");
    } finally {
      setIsSavingHook(false);
    }
  };

  const triggerVercelRebuild = async (silent = false) => {
    const docRef = doc(db, "settings", "vercel");
    const docSnap = await getDoc(docRef);
    let targetHook = vercelHook;
    if (docSnap.exists()) {
      targetHook = docSnap.data().hookUrl || vercelHook;
    }
    
    if (!targetHook) {
      toast.warning("Vercel Deploy Hook is not configured. Please paste your Vercel Deploy Hook URL in the settings panel below to enable automatic page prerendering & Schema updates.");
      return;
    }

    if (!silent) {
      setIsTriggeringRebuild(true);
    }

    try {
      await fetch(targetHook, {
        method: "POST"
      });
      toast.success("🚀 Vercel rebuild triggered! SEO/Schema updates will be live in 2-3 minutes.");
    } catch (error) {
      console.error("Error triggering rebuild:", error);
      toast.error("Failed to trigger Vercel rebuild. Please check your deploy hook URL.");
    } finally {
      if (!silent) {
        setIsTriggeringRebuild(false);
      }
    }
  };

  const fetchOutlets = async () => {
    setIsLoading(true);
    try {
      const q = query(collection(db, "outlets"));
      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setOutlets(data);
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error('Failed to load outlets from Firebase');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === 'pizzatoons2024') {
      setIsAuthenticated(true);
      toast.success('Admin access granted');
    } else {
      toast.error('Invalid password');
    }
  };

  const generateSlug = (name) => {
    return name
      .replace(/PizzaToons /i, "")
      .replace(/PizaaToons /i, "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9 ]/g, "")
      .replace(/\s+/g, "-");
  };

  const determineZone = (lat, lng, address = '') => {
    const addr = (address || '').toLowerCase();
    if (addr.includes('satara')) return 'Satara';
    if (addr.includes('mumbai')) return 'Mumbai';
    if (addr.includes('kolhapur')) return 'Kolhapur';
    if (addr.includes('sangli')) return 'Sangli';
    if (addr.includes('solapur')) return 'Solapur';
    if (addr.includes('nashik')) return 'Nashik';
    if (addr.includes('nagpur')) return 'Nagpur';
    if (addr.includes('thane')) return 'Thane';

    if (lat < 18.3 || lat > 18.8 || lng < 73.6 || lng > 74.2) {
      return 'Other';
    }

    if (lat > 18.58) return "PCMC";
    if (lng > 73.88) return "East Pune";
    if (lat < 18.51) return "South Pune";
    return "West Pune";
  };

  const extractCoords = async (url) => {
    // Basic regex for coordinates in Google Maps URL
    const atMatch = url.match(/@([-+]?\d+\.\d+),([-+]?\d+\.\d+)/);
    if (atMatch) return { lat: parseFloat(atMatch[1]), lng: parseFloat(atMatch[2]) };

    const bangMatch = url.match(/!3d([-+]?\d+\.\d+)!4d([-+]?\d+\.\d+)/);
    if (bangMatch) return { lat: parseFloat(bangMatch[1]), lng: parseFloat(bangMatch[2]) };

    return { lat: null, lng: null };
  };

  const handleLinkOrAddressChange = async (field, value) => {
    const updatedForm = { ...formData, [field]: value };
    
    // Attempt to auto-suggest zone
    let suggestedZone = formData.zone;
    
    const { lat, lng } = await extractCoords(field === 'link' ? value : formData.link);
    const addr = field === 'address' ? value : formData.address;
    
    if (lat && lng) {
      suggestedZone = determineZone(lat, lng, addr);
    } else if (addr) {
      const lowerAddr = addr.toLowerCase();
      if (lowerAddr.includes('satara')) suggestedZone = 'Satara';
      else if (lowerAddr.includes('mumbai')) suggestedZone = 'Mumbai';
      else if (lowerAddr.includes('kolhapur')) suggestedZone = 'Kolhapur';
    }
    
    setFormData({
      ...updatedForm,
      zone: suggestedZone
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { lat, lng } = await extractCoords(formData.link);
      
      if (!lat || !lng) {
        toast.error("Could not extract coordinates from link. Please use a full Google Maps URL with '@lat,lng'.");
        setIsLoading(false);
        return;
      }

      const slug = generateSlug(formData.name);
      const zone = formData.zone.trim() || determineZone(lat, lng, formData.address);

      const newOutlet = {
        name: formData.name,
        address: formData.address,
        phone: formData.phone,
        lat,
        lng,
        zone,
        slug,
        isHeadOffice: formData.isHeadOffice,
        createdAt: new Date()
      };

      await addDoc(collection(db, "outlets"), newOutlet);
      
      toast.success(`Outlet "${formData.name}" added to Firebase!`);
      setFormData({ name: '', address: '', link: '', phone: '', zone: '', isHeadOffice: false });
      fetchOutlets();
      triggerVercelRebuild(true);
    } catch (error) {
      console.error("Add error:", error);
      toast.error('Failed to add outlet');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = async (id) => {
    try {
      const outletRef = doc(db, "outlets", id);
      await updateDoc(outletRef, editData);
      toast.success('Outlet updated in Firebase');
      setEditingId(null);
      fetchOutlets();
      triggerVercelRebuild(true);
    } catch (error) {
      toast.error('Update failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this outlet?')) return;

    try {
      await deleteDoc(doc(db, "outlets", id));
      toast.success('Outlet deleted from Firebase');
      fetchOutlets();
      triggerVercelRebuild(true);
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const filteredOutlets = outlets.filter(o => {
    const nameMatch = (o.name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const addressMatch = (o.address || '').toLowerCase().includes(searchTerm.toLowerCase());
    const zoneMatch = (o.zone || '').toLowerCase().includes(searchTerm.toLowerCase());
    return nameMatch || addressMatch || zoneMatch;
  }).sort((a, b) => (b.id || 0) - (a.id || 0));


  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md bg-card border border-border rounded-2xl p-8 shadow-xl">
          <div className="flex flex-col items-center mb-8">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <Lock className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-2xl font-bold">Admin Login</h1>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="password"
              className="w-full h-11 px-4 rounded-xl bg-muted border border-border outline-none transition-all"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <button type="submit" className="w-full h-11 bg-primary text-primary-foreground font-semibold rounded-xl">
              Access Dashboard
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6 md:p-12 lg:p-20">
      <div className="max-w-6xl mx-auto space-y-16">
        <header className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <h1 className="text-5xl font-black mb-2 tracking-tight">Admin Console</h1>
            <p className="text-muted-foreground text-xl">Manage PizzaToons Outlets (Firebase)</p>
          </div>
          <div className="flex items-center gap-4">
            <button 
              onClick={async () => {
                if (window.confirm("This will upload all outlets from outlets.json to Firebase, skipping duplicates. Continue?")) {
                  setIsLoading(true);
                  try {
                    const { outlets: jsonOutlets } = require('../data/mock');
                    let addedCount = 0;
                    let skippedCount = 0;

                    for (const outlet of jsonOutlets) {
                      const { id, ...data } = outlet;
                      // Check if slug already exists in Firestore
                      const q = query(collection(db, "outlets"), where("slug", "==", outlet.slug));
                      const existing = await getDocs(q);
                      
                      if (existing.empty) {
                        await addDoc(collection(db, "outlets"), {
                          ...data,
                          createdAt: new Date()
                        });
                        addedCount++;
                      } else {
                        skippedCount++;
                      }
                    }
                    toast.success(`Sync complete! Added ${addedCount}, Skipped ${skippedCount} duplicates.`);
                    fetchOutlets();
                    triggerVercelRebuild(true);
                  } catch (err) {
                    console.error(err);
                    toast.error("Sync failed");
                  } finally {
                    setIsLoading(false);
                  }
                }
              }} 
              className="px-6 py-2 rounded-full border border-primary text-primary hover:bg-primary/5 transition-colors text-sm font-bold flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" /> Smart Sync
            </button>

            <button 
              onClick={async () => {
                if (window.confirm("CRITICAL: This will delete ALL outlets from Firebase. This cannot be undone. Are you sure?")) {
                  setIsLoading(true);
                  try {
                    const q = query(collection(db, "outlets"));
                    const querySnapshot = await getDocs(q);
                    const batch = writeBatch(db);
                    querySnapshot.docs.forEach((doc) => {
                      batch.delete(doc.ref);
                    });
                    await batch.commit();
                    toast.success("All outlets cleared from Firebase!");
                    fetchOutlets();
                    triggerVercelRebuild(true);
                  } catch (err) {
                    toast.error("Clear failed");
                  } finally {
                    setIsLoading(false);
                  }
                }
              }} 
              className="px-6 py-2 rounded-full border border-destructive text-destructive hover:bg-destructive/5 transition-colors text-sm font-bold flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" /> Clear All (Live)
            </button>
            <button onClick={() => setIsAuthenticated(false)} className="px-6 py-2 rounded-full border border-border hover:bg-muted transition-colors text-sm font-bold">
              Log Out
            </button>
          </div>
        </header>

        {/* List Section */}
        {isLoading && (
          <div className="flex justify-center py-10">
            <Loader2 className="w-10 h-10 animate-spin text-primary" />
          </div>
        )}

        {/* Add New Outlet Section */}
        <section className="bg-card border border-border rounded-[2.5rem] p-8 md:p-12 shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-80 h-80 bg-primary/5 rounded-full -mr-40 -mt-40 blur-[80px]" />
          <h2 className="text-2xl font-bold mb-8 flex items-center gap-3">
            <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center text-primary-foreground">
              <Plus className="w-6 h-6" />
            </div>
            Add New Outlet
          </h2>
          
          <form onSubmit={handleSubmit} className="relative grid md:grid-cols-2 gap-8 text-left">
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="text-xs font-black uppercase tracking-widest text-muted-foreground ml-1">Name</label>
                <input
                  type="text"
                  placeholder="PizzaToons Kharadi"
                  className="w-full h-14 px-6 rounded-2xl bg-muted border-2 border-transparent focus:border-primary/20 outline-none transition-all font-medium text-lg"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-black uppercase tracking-widest text-muted-foreground ml-1">Address</label>
                <textarea
                  placeholder="Street address..."
                  className="w-full p-6 rounded-2xl bg-muted border-2 border-transparent focus:border-primary/20 outline-none transition-all font-medium resize-none min-h-[120px]"
                  value={formData.address}
                  onChange={(e) => handleLinkOrAddressChange('address', e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="space-y-6">
              <div className="space-y-2">
                <label className="text-xs font-black uppercase tracking-widest text-muted-foreground ml-1">Maps Link (with Coordinates)</label>
                <input
                  type="url"
                  placeholder="https://maps.app.goo.gl/..."
                  className="w-full h-14 px-6 rounded-2xl bg-muted border-2 border-transparent focus:border-primary/20 outline-none transition-all font-medium"
                  value={formData.link}
                  onChange={(e) => handleLinkOrAddressChange('link', e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-black uppercase tracking-widest text-muted-foreground ml-1">Zone (Auto-suggested / Editable)</label>
                <input
                  type="text"
                  placeholder="e.g. Satara, East Pune, PCMC"
                  className="w-full h-14 px-6 rounded-2xl bg-muted border-2 border-transparent focus:border-primary/20 outline-none transition-all font-medium"
                  value={formData.zone}
                  onChange={(e) => setFormData({ ...formData, zone: e.target.value })}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-xs font-black uppercase tracking-widest text-muted-foreground ml-1">Phone</label>
                  <input
                    type="text"
                    placeholder="8007..."
                    className="w-full h-14 px-6 rounded-2xl bg-muted border-2 border-transparent focus:border-primary/20 outline-none transition-all font-medium"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
                <div className="flex flex-col justify-center">
                  <label className="flex items-center gap-3 cursor-pointer group mt-4">
                    <input
                      type="checkbox"
                      className="w-6 h-6 rounded-lg bg-muted border-border text-primary focus:ring-offset-0"
                      checked={formData.isHeadOffice}
                      onChange={(e) => setFormData({ ...formData, isHeadOffice: e.target.checked })}
                    />
                    <span className="font-bold group-hover:text-primary transition-colors">Head Office</span>
                  </label>
                </div>
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full h-16 bg-primary text-primary-foreground font-black text-xl rounded-2xl hover:scale-[1.02] active:scale-[0.98] transition-all shadow-xl shadow-primary/20 flex items-center justify-center gap-3"
              >
                {isLoading ? <Loader2 className="w-6 h-6 animate-spin" /> : <><Plus className="w-6 h-6" /> Add Outlet</>}
              </button>
            </div>
          </form>
        </section>

        {/* Vercel Deploy Hook & Publishing Config */}
        <section className="bg-card border border-border rounded-[2.5rem] p-8 md:p-12 shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-80 h-80 bg-orange-500/5 rounded-full -mr-40 -mt-40 blur-[80px]" />
          <h2 className="text-2xl font-bold mb-8 flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-500/10 rounded-xl flex items-center justify-center text-orange-600">
              <RefreshCw className="w-6 h-6" />
            </div>
            Publishing & SEO Deploy Settings
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8 items-start relative z-10 text-left">
            <div className="md:col-span-2 space-y-6">
              <p className="text-muted-foreground text-sm leading-relaxed">
                PizzaToons uses pre-rendered static pages for peak performance and Schema.org SEO compliance. 
                Whenever you add, modify, or delete a store, you can trigger a Vercel rebuild to generate the new pre-rendered page and its metadata schema. This hook will trigger automatically on modifications.
              </p>
              <form onSubmit={handleSaveHook} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-xs font-black uppercase tracking-widest text-muted-foreground ml-1">Vercel Deploy Hook URL</label>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <input
                      type="url"
                      placeholder="https://api.vercel.com/v1/integrations/deploy/..."
                      className="flex-1 h-14 px-6 rounded-2xl bg-muted border-2 border-transparent focus:border-primary/20 outline-none transition-all font-medium text-sm"
                      value={vercelHook}
                      onChange={(e) => setVercelHook(e.target.value)}
                    />
                    <button
                      type="submit"
                      disabled={isSavingHook}
                      className="px-6 h-14 bg-primary text-primary-foreground font-bold rounded-2xl hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50"
                    >
                      {isSavingHook ? 'Saving...' : 'Save Hook'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
            
            <div className="p-8 bg-muted/50 border border-border rounded-3xl space-y-6 flex flex-col justify-between h-full">
              <div>
                <h3 className="text-lg font-bold mb-2">Manual Rebuild</h3>
                <p className="text-muted-foreground text-xs leading-relaxed">
                  Trigger an immediate deployment build on Vercel manually to sync all schemas and store images.
                </p>
              </div>
              <button
                onClick={() => triggerVercelRebuild(false)}
                disabled={isTriggeringRebuild || !vercelHook}
                className="w-full h-14 bg-orange-600 text-white font-bold rounded-2xl hover:scale-[1.02] active:scale-[0.98] transition-all shadow-xl shadow-orange-600/20 disabled:opacity-50 disabled:pointer-events-none flex items-center justify-center gap-2"
              >
                {isTriggeringRebuild ? <Loader2 className="w-5 h-5 animate-spin" /> : '🚀 Trigger Vercel Rebuild'}
              </button>
            </div>
          </div>
        </section>

        {/* List Section */}
        <section className="space-y-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <h2 className="text-3xl font-bold flex items-center gap-3">
              Existing Outlets <span className="bg-muted px-4 py-1 rounded-full text-sm">{outlets.length}</span>
            </h2>
            <div className="relative w-full md:w-96 group">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground group-focus-within:text-primary transition-colors" />
              <input
                type="text"
                placeholder="Search by name, zone or address..."
                className="w-full h-12 pl-12 pr-4 rounded-full bg-card border border-border focus:border-primary/50 outline-none transition-all"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="grid gap-4">
            {filteredOutlets.map(outlet => (
              <div key={outlet.id} className="bg-card border border-border rounded-3xl p-6 transition-all hover:border-primary/30 group">
                {editingId === outlet.id ? (
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="space-y-4 md:col-span-2">
                       <input 
                         className="w-full bg-muted p-3 rounded-xl font-bold border-2 border-primary/20 outline-none"
                         value={editData.name}
                         onChange={e => setEditData({...editData, name: e.target.value})}
                       />
                       <textarea 
                         className="w-full bg-muted p-3 rounded-xl border-2 border-primary/20 outline-none"
                         rows="2"
                         value={editData.address}
                         onChange={e => setEditData({...editData, address: e.target.value})}
                       />
                        <div className="grid grid-cols-2 gap-4">
                          <input 
                            className="w-full bg-muted p-3 rounded-xl border-2 border-primary/20 outline-none"
                            placeholder="Phone"
                            value={editData.phone || ''}
                            onChange={e => setEditData({...editData, phone: e.target.value})}
                          />
                          <input 
                             className="w-full bg-muted p-3 rounded-xl border-2 border-primary/20 outline-none"
                             placeholder="Slug (url-path)"
                             value={editData.slug || ''}
                             onChange={e => setEditData({...editData, slug: e.target.value})}
                           />
                        </div>
                        <div className="grid grid-cols-1">
                          <input 
                            className="w-full bg-muted p-3 rounded-xl border-2 border-primary/20 outline-none"
                            placeholder="Zone (e.g. Satara, East Pune)"
                            value={editData.zone}
                            onChange={e => setEditData({...editData, zone: e.target.value})}
                          />
                        </div>
                    </div>
                    <div className="flex flex-col gap-3 justify-center">
                      <button onClick={() => handleUpdate(outlet.id)} className="w-full h-12 bg-primary text-primary-foreground font-bold rounded-xl flex items-center justify-center gap-2">
                        <Check className="w-5 h-5" /> Save Changes
                      </button>
                      <button onClick={() => setEditingId(null)} className="w-full h-12 bg-muted hover:bg-muted-foreground/10 font-bold rounded-xl flex items-center justify-center gap-2">
                        <X className="w-5 h-5" /> Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col md:flex-row justify-between gap-6">
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <h3 className="text-xl font-bold">{outlet.name}</h3>
                        {outlet.isHeadOffice && <span className="bg-primary/10 text-primary text-[10px] uppercase font-black px-2 py-1 rounded-md">Head Office</span>}
                        <span className="text-xs bg-muted px-2 py-1 rounded text-muted-foreground font-bold">{outlet.zone}</span>
                      </div>
                      <p className="text-muted-foreground text-sm max-w-2xl">{outlet.address}</p>
                      <div className="flex items-center gap-4 text-xs font-bold text-muted-foreground">
                         <span className="flex items-center gap-1"><MapPin className="w-3 h-3"/> {outlet.lat.toFixed(4)}, {outlet.lng.toFixed(4)}</span>
                         {outlet.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3"/> {outlet.phone}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 md:opacity-0 group-hover:opacity-100 transition-opacity">
                      <a 
                        href={`/${outlet.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 h-10 rounded-full bg-muted flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-all"
                        title="View Public Page"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                      <button 
                        onClick={() => {
                          setEditingId(outlet.id);
                          setEditData({...outlet});
                        }}
                        className="w-10 h-10 rounded-full bg-muted flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-all"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => handleDelete(outlet.id)}
                        className="w-10 h-10 rounded-full bg-muted flex items-center justify-center hover:bg-destructive hover:text-destructive-foreground transition-all"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminOutletDashboard;

